import type { RealmDTO } from '@jianghu/protocol';
import { useEffect, useState } from 'react';
import { Logo } from '../components/Logo';
import { audio } from '../game/audio/audio';
import { api } from '../services/api';
import { rememberedRealmId, useSession } from '../stores/session-store';

/** Server (realm) selection. */
export function RealmScreen() {
  const [realms, setRealms] = useState<RealmDTO[] | null>(null);
  const [selected, setSelected] = useState<string | null>(rememberedRealmId());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .realms()
      .then((r) => {
        setRealms(r);
        setSelected((cur) => (cur && r.some((x) => x.id === cur) ? cur : (r[0]?.id ?? null)));
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Failed to load realms'));
  }, []);

  const go = (): void => {
    const realm = realms?.find((r) => r.id === selected);
    if (!realm) return;
    audio.play('ui_confirm');
    useSession.getState().setRealm(realm);
    useSession.getState().go('characters');
  };

  return (
    <main className="screen">
      <div className="col" style={{ alignItems: 'center', width: '100%' }}>
        <Logo compact />
        <section className="panel" style={{ width: 'min(460px, 100%)' }} aria-labelledby="realm-title">
          <h2 className="panel-title" id="realm-title">
            Choose a Realm
          </h2>
          {error && <div className="form-error">{error}</div>}
          {!realms && !error && <div className="spinner" style={{ margin: '20px auto' }} />}
          <div className="realm-list" role="listbox" aria-label="Realms">
            {realms?.map((r) => (
              <button
                key={r.id}
                className="realm-item"
                role="option"
                aria-selected={selected === r.id}
                aria-pressed={selected === r.id}
                onClick={() => setSelected(r.id)}
                onDoubleClick={go}
              >
                <span className={`status-dot ${r.status === 'full' ? 'full' : ''}`} />
                <span>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: 17 }}>
                    {r.name} <span className="gold">{r.nameZh}</span>
                  </div>
                  <div className="muted" style={{ fontSize: 12 }}>
                    {r.region} · {r.status === 'full' ? 'Full' : 'Online'}
                  </div>
                </span>
                <span className="spacer" />
                <span className="muted" style={{ fontSize: 12 }}>
                  {r.online} / {r.capacity}
                </span>
              </button>
            ))}
          </div>
          <div className="row" style={{ marginTop: 18 }}>
            <button className="btn btn-ghost" onClick={() => useSession.getState().go('menu')}>
              Back
            </button>
            <span className="spacer" />
            <button className="btn btn-primary" disabled={!selected} onClick={go}>
              Continue
            </button>
          </div>
        </section>
      </div>
    </main>
  );
}
